import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, TOKEN_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import CRM from "@/pages/CRM";
import Calendar from "@/pages/Calendar";
import Tasks from "@/pages/Tasks";
import Inventory from "@/pages/Inventory";
import Deliverables from "@/pages/Deliverables";
import Travel from "@/pages/Travel";
import Portal from "@/pages/Portal";
import Finance from "@/pages/Finance";
import Marketing from "@/pages/Marketing";
import AIHub from "@/pages/AIHub";
import Settings from "@/pages/Settings";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Join from "@/pages/Join";
import { consumePendingInvite } from "@/lib/invite";

function AuthCallback() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sessionId) {
      navigate("/login");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        localStorage.setItem(TOKEN_KEY, data.token);
        setSession(data.token, data.user);
        window.history.replaceState(null, "", window.location.pathname);
        const joined = await consumePendingInvite();
        navigate(joined || data.user.onboarded ? "/dashboard" : "/onboarding", { replace: true });
      } catch {
        navigate("/login");
      }
    })();
  }, [navigate, setSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function withLayout(el) {
  return (
    <ProtectedRoute>
      <Layout>{el}</Layout>
    </ProtectedRoute>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<Register />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/join/:token" element={<Join />} />
      <Route path="/portal/:token" element={<Portal />} />
      <Route path="/onboarding" element={<ProtectedRoute requireOnboarded={false}><Onboarding /></ProtectedRoute>} />
      <Route path="/dashboard" element={withLayout(<Dashboard />)} />
      <Route path="/clients" element={withLayout(<CRM />)} />
      <Route path="/crm" element={<Navigate to="/clients" replace />} />
      <Route path="/calendar" element={withLayout(<Calendar />)} />
      <Route path="/tasks" element={withLayout(<Tasks />)} />
      <Route path="/inventory" element={withLayout(<Inventory />)} />
      <Route path="/deliverables" element={withLayout(<Deliverables />)} />
      <Route path="/travel" element={withLayout(<Travel />)} />
      <Route path="/operations" element={<Navigate to="/tasks" replace />} />
      <Route path="/finance" element={withLayout(<Finance />)} />
      <Route path="/marketing" element={withLayout(<Marketing />)} />
      <Route path="/ai-hub" element={withLayout(<AIHub />)} />
      <Route path="/settings" element={withLayout(<Settings />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}
